import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { eventsAPI } from '../../services/eventsApi';

// ─── Types ────────────────────────────────────────────────────────────────────

export type EventStatus =
  | 'draft' | 'published' | 'upcoming' | 'in_progress'
  | 'completed' | 'cancelled' | 'postponed';

export type RegistrationStatus =
  | 'pending' | 'approved' | 'declined' | 'waitlisted' | 'cancelled';

export type VerificationStatus =
  | 'not_required' | 'pending' | 'verified' | 'rejected';

export type AttendanceStatus = 'registered' | 'attended' | 'no_show';

export interface EventDocument {
  id: string;
  event_id: string;
  file_name: string;
  file_path: string;
  file_type: 'image' | 'document' | 'poster';
  file_size?: number;
  uploaded_at: string;
}

export interface Event {
  id: string;
  organization_id: string;
  title: string;
  slug: string;
  category?: string;
  summary?: string;
  description?: string;
  location?: string;
  venue_name?: string;
  is_virtual: boolean;
  virtual_link?: string;
  start_date: string;
  end_date?: string;
  status: EventStatus;
  capacity?: number;
  registration_required: boolean;
  registration_deadline?: string;
  is_free: boolean;
  price?: number;
  currency: string;
  require_proof_of_payment: boolean;
  require_id_verification: boolean;
  require_facial_verification: boolean;
  require_manual_approval: boolean;
  featured_image?: string;
  poster_image?: string;
  author_name?: string;
  registration_count?: number;
  documents?: EventDocument[];
  created_at: string;
  updated_at: string;
}

export interface EventRegistration {
  id: string;
  event_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  registration_status: RegistrationStatus;
  attendance_status: AttendanceStatus;
  proof_of_payment_path?: string;
  proof_of_payment_name?: string;
  payment_verified: boolean;
  payment_verified_by?: string;
  payment_verified_at?: string;
  payment_notes?: string;
  id_document_path?: string;
  id_document_name?: string;
  facial_image_path?: string;
  verification_status: VerificationStatus;
  verified_by?: string;
  verified_at?: string;
  verification_notes?: string;
  reviewed_by?: string;
  reviewed_at?: string;
  review_notes?: string;
  reviewer_name?: string;
  created_at: string;
  updated_at: string;
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

interface EventsState {
  events: Event[];
  currentEvent: Event | null;
  registrations: EventRegistration[];
  currentRegistration: EventRegistration | null;
  pagination: Pagination;
  registrationPagination: Pagination;
  loading: boolean;
  registrationsLoading: boolean;
  error: string | null;
}

// ─── Initial State ────────────────────────────────────────────────────────────

const initialState: EventsState = {
  events: [],
  currentEvent: null,
  registrations: [],
  currentRegistration: null,
  pagination: { total: 0, page: 1, limit: 20, pages: 0 },
  registrationPagination: { total: 0, page: 1, limit: 50, pages: 0 },
  loading: false,
  registrationsLoading: false,
  error: null,
};

// ─── Error extractor — always returns a plain string ─────────────────────────
// Prevents raw Error objects / Axios error objects reaching React as children.

const extractMessage = (e: any, fallback: string): string => {
  if (typeof e === 'string') return e;
  if (e?.response?.data?.message) return String(e.response.data.message);
  if (e?.message) return String(e.message);
  return fallback;
};

// ─── Thunks ───────────────────────────────────────────────────────────────────

export const fetchEvents = createAsyncThunk(
  'events/fetchAll',
  async (params: Parameters<typeof eventsAPI.getAll>[0], { rejectWithValue }) => {
    try {
      const res = await eventsAPI.getAll(params);
      return res.data;
    } catch (e: any) {
      return rejectWithValue(extractMessage(e, 'Failed to fetch events'));
    }
  }
);

export const fetchEventById = createAsyncThunk(
  'events/fetchById',
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await eventsAPI.getById(id);
      return res.data;
    } catch (e: any) {
      return rejectWithValue(extractMessage(e, 'Failed to fetch event'));
    }
  }
);

export const createEvent = createAsyncThunk(
  'events/create',
  async (data: Record<string, any>, { rejectWithValue }) => {
    try {
      const res = await eventsAPI.create(data);
      return res.data;
    } catch (e: any) {
      return rejectWithValue(extractMessage(e, 'Failed to create event'));
    }
  }
);

export const updateEvent = createAsyncThunk(
  'events/update',
  async ({ id, data }: { id: string; data: Record<string, any> }, { rejectWithValue }) => {
    try {
      const res = await eventsAPI.update(id, data);
      return res.data;
    } catch (e: any) {
      return rejectWithValue(extractMessage(e, 'Failed to update event'));
    }
  }
);

export const deleteEvent = createAsyncThunk(
  'events/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await eventsAPI.delete(id);
      return id;
    } catch (e: any) {
      return rejectWithValue(extractMessage(e, 'Failed to delete event'));
    }
  }
);

export const fetchRegistrations = createAsyncThunk(
  'events/fetchRegistrations',
  async (
    { eventId, params }: { eventId: string; params?: Parameters<typeof eventsAPI.getRegistrations>[1] },
    { rejectWithValue }
  ) => {
    try {
      const res = await eventsAPI.getRegistrations(eventId, params);
      return res.data;
    } catch (e: any) {
      return rejectWithValue(extractMessage(e, 'Failed to fetch registrations'));
    }
  }
);

export const updateRegistrationStatus = createAsyncThunk(
  'events/updateRegistrationStatus',
  async (
    { eventId, regId, data }: {
      eventId: string;
      regId: string;
      data: { registration_status: string; review_notes?: string };
    },
    { rejectWithValue }
  ) => {
    try {
      await eventsAPI.updateRegistrationStatus(eventId, regId, data);
      return { regId, ...data };
    } catch (e: any) {
      return rejectWithValue(extractMessage(e, 'Failed to update status'));
    }
  }
);

export const bulkUpdateStatus = createAsyncThunk(
  'events/bulkUpdateStatus',
  async (
    { eventId, data }: { eventId: string; data: { registration_ids: string[]; registration_status: string; review_notes: any } },
    { rejectWithValue }
  ) => {
    try {
      await eventsAPI.bulkUpdateStatus(eventId, data);
      return data;
    } catch (e: any) {
      return rejectWithValue(extractMessage(e, 'Bulk update failed'));
    }
  }
);

export const updateAttendance = createAsyncThunk(
  'events/updateAttendance',
  async (
    { eventId, regId, attendance_status }: { eventId: string; regId: string; attendance_status: string },
    { rejectWithValue }
  ) => {
    try {
      await eventsAPI.updateAttendance(eventId, regId, attendance_status);
      return { regId, attendance_status };
    } catch (e: any) {
      return rejectWithValue(extractMessage(e, 'Failed to update attendance'));
    }
  }
);

export const verifyPayment = createAsyncThunk(
  'events/verifyPayment',
  async (
    { eventId, regId, data }: { eventId: string; regId: string; data: { payment_verified: boolean; payment_notes?: string } },
    { rejectWithValue }
  ) => {
    try {
      await eventsAPI.verifyPayment(eventId, regId, data);
      return { regId, ...data };
    } catch (e: any) {
      return rejectWithValue(extractMessage(e, 'Failed to verify payment'));
    }
  }
);

export const verifyIdentity = createAsyncThunk(
  'events/verifyIdentity',
  async (
    { eventId, regId, data }: { eventId: string; regId: string; data: { verification_status: string; verification_notes?: string } },
    { rejectWithValue }
  ) => {
    try {
      await eventsAPI.verifyIdentity(eventId, regId, data);
      return { regId, ...data };
    } catch (e: any) {
      return rejectWithValue(extractMessage(e, 'Failed to verify identity'));
    }
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const eventsSlice = createSlice({
  name: 'events',
  initialState,
  reducers: {
    clearCurrentEvent: (state) => { state.currentEvent = null; },
    clearError: (state) => { state.error = null; },
    clearRegistrations: (state) => { state.registrations = []; },
  },
  extraReducers: (builder) => {
    // fetchEvents
    builder
      .addCase(fetchEvents.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        state.loading = false;
        state.events = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // fetchEventById
    builder
      .addCase(fetchEventById.pending, (state) => { state.loading = true; })
      .addCase(fetchEventById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentEvent = action.payload;
      })
      .addCase(fetchEventById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // createEvent
    builder
      .addCase(createEvent.fulfilled, (state, action) => {
        state.events.unshift(action.payload);
      });

    // updateEvent
    builder
      .addCase(updateEvent.fulfilled, (state, action) => {
        const idx = state.events.findIndex(e => e.id === action.payload.id);
        if (idx !== -1) state.events[idx] = action.payload;
        if (state.currentEvent?.id === action.payload.id) state.currentEvent = action.payload;
      });

    // deleteEvent
    builder
      .addCase(deleteEvent.fulfilled, (state, action) => {
        state.events = state.events.filter(e => e.id !== action.payload);
        if (state.currentEvent?.id === action.payload) state.currentEvent = null;
      });

    // fetchRegistrations
    builder
      .addCase(fetchRegistrations.pending, (state) => { state.registrationsLoading = true; })
      .addCase(fetchRegistrations.fulfilled, (state, action) => {
        state.registrationsLoading = false;
        state.registrations = action.payload.data;
        state.registrationPagination = action.payload.pagination;
      })
      .addCase(fetchRegistrations.rejected, (state, action) => {
        state.registrationsLoading = false;
        state.error = action.payload as string;
      });

    // updateRegistrationStatus
    builder.addCase(updateRegistrationStatus.fulfilled, (state, action) => {
      const reg = state.registrations.find(r => r.id === action.payload.regId);
      if (reg) {
        reg.registration_status = action.payload.registration_status as RegistrationStatus;
        if (action.payload.review_notes) reg.review_notes = action.payload.review_notes;
      }
    });

    // bulkUpdateStatus
    builder.addCase(bulkUpdateStatus.fulfilled, (state, action) => {
      action.payload.registration_ids.forEach(id => {
        const reg = state.registrations.find(r => r.id === id);
        if (reg) reg.registration_status = action.payload.registration_status as RegistrationStatus;
      });
    });

    // updateAttendance
    builder.addCase(updateAttendance.fulfilled, (state, action) => {
      const reg = state.registrations.find(r => r.id === action.payload.regId);
      if (reg) reg.attendance_status = action.payload.attendance_status as AttendanceStatus;
    });

    // verifyPayment
    builder.addCase(verifyPayment.fulfilled, (state, action) => {
      const reg = state.registrations.find(r => r.id === action.payload.regId);
      if (reg) {
        reg.payment_verified = action.payload.payment_verified;
        if (action.payload.payment_notes) reg.payment_notes = action.payload.payment_notes;
      }
    });

    // verifyIdentity
    builder.addCase(verifyIdentity.fulfilled, (state, action) => {
      const reg = state.registrations.find(r => r.id === action.payload.regId);
      if (reg) {
        reg.verification_status = action.payload.verification_status as VerificationStatus;
        if (action.payload.verification_notes) reg.verification_notes = action.payload.verification_notes;
      }
    });
  },
});

export const { clearCurrentEvent, clearError, clearRegistrations } = eventsSlice.actions;
export default eventsSlice.reducer;